import { StringContext } from "../StringContext";
import { ScrollController } from "./ScrollController";

type SmoothComputeStepResult = {
  current: number;
  target: number;
  delta: number;
  lerped: number;
  scrollForce: number;
  absVelocity: number;
};

/**
 * Smooth scroll controller with delta-based acceleration and direction tracking.
 * Handles wheel events and scroll position updates with smooth interpolation.
 */
export class StringScrollSmooth extends ScrollController {
  /**
   * Unique identifier for this scroll controller type.
   * Used for selection and debug purposes.
   */
  public readonly name: string = "smooth";

  /**
   * Whether the user has manually scrolled using the native scrollbar.
   * When true, the scroll position is directly synced from DOM on next frame.
   */
  private isScrollbarManipulation = false;

  /**
   * Current internal force applied to the scroll target.
   * Calculated from accumulated scroll impulse and acceleration.
   */
  private scrollForce: number = 0;

  /**
   * Latest raw scroll impulse received from the wheel event (`deltaY`).
   * Temporarily stored for direction and boundary checks.
   */
  private wheelImpulse: number = 0;

  /**
   * Scroll position from the previous frame.
   * Used to detect actual movement and trigger updates only when needed.
   */
  private previousCurrent: number = 0;

  /**
   * Minimum velocity threshold below which scrolling is considered stopped.
   * Prevents micro-movements from continuing infinite smooth scroll.
   */
  private readonly velocityThreshold = 0.1;
  private readonly stepResult: SmoothComputeStepResult = {
    current: 0,
    target: 0,
    delta: 0,
    lerped: 0,
    scrollForce: 0,
    absVelocity: 0,
  };

  constructor(context: StringContext) {
    super(context);
  }

  /**
   * Immediately stops scrolling and resets all deltas and directions.
   */
  public stopScroll(): void {
    this.context.data.scroll.lerped = 0;
    this.context.data.scroll.delta = 0;
    this.context.data.scroll.target = this.context.data.scroll.current;
    this.isProg = false;
    this.onCalcUpdate();
    this.clearScrollingClasses();
    this.isBottomScrollDirection = null;
    this.lastAppliedDirection = null;
  }

  /**
   * Called on each animation frame to apply lerped scroll logic.
   */
  public onFrame(): void {
    if (this.isScrollbarManipulation) {
      this.isScrollbarManipulation = false;
      this.context.data.scroll.current = this.context.data.scroll.elementContainer.scrollTop;
      this.context.data.scroll.target = this.context.data.scroll.elementContainer.scrollTop;
      this.context.data.scroll.transformedCurrent =
        this.context.data.scroll.current * this.context.data.viewport.transformScale;
      return;
    }

    if (this.context.data.scroll.delta !== 0) {
      this.computeStep(
        this.context.data.scroll.current,
        this.context.data.scroll.target,
        this.context.data.scroll.delta,
        this.context.data.scroll.speed,
        this.context.data.scroll.speedAccelerate,
        this.context.data.scroll.bottomPosition,
        this.stepResult
      );

      this.scrollForce = this.stepResult.scrollForce;
      this.context.data.scroll.target = this.stepResult.target;
      this.context.data.scroll.delta = this.stepResult.delta;
      this.context.data.scroll.lerped = this.stepResult.lerped;
      this.context.data.scroll.current = this.stepResult.current;

      this.context.data.scroll.transformedCurrent =
        this.context.data.scroll.current * this.context.data.viewport.transformScale;
      this.updateScrollDirection(this.context.data.scroll.lerped > 0);

      if (this.stepResult.absVelocity < this.velocityThreshold) {
        this.stopScroll();
        this.onScrollStop();
      } else {
        this.isProg = true;
        if (this.previousCurrent !== this.context.data.scroll.current) {
          this.previousCurrent = this.context.data.scroll.current;
          this.onCalcUpdate();
        }
      }
    }
  }

  /**
   * Handles user wheel input to accumulate scroll delta.
   * @param e The WheelEvent from the browser.
   */
  public onWheel(e: WheelEvent): void {
    if (e.deltaY !== 0) {
      e.preventDefault();
    }

    this.wheelImpulse = e.deltaY;
    if (this.wheelImpulse === 0) return;

    if (this.context.data.scroll.delta === 0) {
      this.onScrollStart();
    }

    const scrollDirection = Math.sign(this.wheelImpulse);
    const atTop = this.context.data.scroll.target === 0 && scrollDirection < 0;
    const atBottom =
      this.context.data.scroll.target === this.context.data.scroll.bottomPosition &&
      scrollDirection > 0;

    if (atTop || atBottom) return;
    this.context.data.scroll.delta += this.wheelImpulse;
  }

  /**
   * Detects native scrollbar manipulation when smooth scroll is idle.
   * @param e Scroll event.
   */
  public onScroll(e: Event): void {
    if (!this.isProg) {
      this.isScrollbarManipulation = true;
    }
  }

  public deactivate(): void {
    super.deactivate();
    this.isScrollbarManipulation = false;
    this.scrollForce = 0;
    this.wheelImpulse = 0;
    this.previousCurrent = this.context.data.scroll.current;
  }

  public scrollTo(position: number, immediate?: boolean) {
    if (immediate) {
      this.context.data.scroll.current = position;
      this.context.data.scroll.target = position;
      this.context.data.scroll.delta = 0;
      this.context.data.scroll.lerped = 0;
      this.context.data.scroll.transformedCurrent =
        this.context.data.scroll.current * this.context.data.viewport.transformScale;
      this.onCalcUpdate();
      return;
    }

    this.context.data.scroll.target = position;
    this.context.data.scroll.delta = 1;
  }

  private computeStep(
    current: number,
    target: number,
    delta: number,
    speed: number,
    speedAccelerate: number,
    bottomPosition: number,
    out: SmoothComputeStepResult
  ): void {
    const scrollForce = delta * speedAccelerate;
    const nextTarget = Math.min(Math.max(0, target + scrollForce), bottomPosition);
    const nextDelta = delta - scrollForce;
    const lerped = (nextTarget - current) * speed;
    const nextCurrent = lerped > 0 ? Math.ceil(current + lerped) : Math.floor(current + lerped);

    out.current = nextCurrent;
    out.target = nextTarget;
    out.delta = nextDelta;
    out.lerped = lerped;
    out.scrollForce = scrollForce;
    out.absVelocity = Math.abs(lerped);
  }
}
